import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Panel } from "@/components/KpiCard";
import { PlatformBadge } from "@/components/Badges";
import { SmartTable } from "@/components/SmartTable";
import { api, money } from "@/lib/api";

const STATUT = { en_attente: "En attente", acceptee: "Acceptée", refusee: "Refusée" };
const pct = (v) => (v == null ? "—" : `${v.toFixed(1).replace(".", ",")} %`);
const Th = ({ children, left }) => <th className={`px-3 py-2 ${left ? "text-left" : "text-right"}`}>{children}</th>;
const Td = ({ children, cls = "", exp }) => <td className={`px-3 py-2.5 text-right tabular ${cls}`} data-st-export={exp}>{children}</td>;

const LigneTotal = ({ l, total }) => (
  <tr className={total ? "border-t-2 border-slate-300 font-semibold" : "border-t border-slate-100"} data-testid={`bilan-reclam-${l.plateforme.toLowerCase()}`}>
    <td className="px-3 py-2.5">{total ? "Total" : <PlatformBadge plateforme={l.plateforme} />}</td>
    <Td>{l.nb}</Td><Td>{money(l.reclame)}</Td>
    <Td cls="text-emerald-700" exp={money(l.accepte)}>{money(l.accepte)} <span className="text-xs font-normal text-slate-500">({l.nb_acceptees})</span></Td>
    <Td cls="text-red-700" exp={money(l.refuse)}>{money(l.refuse)} <span className="text-xs font-normal text-slate-500">({l.nb_refusees})</span></Td>
    <Td cls="text-amber-700" exp={money(l.en_attente)}>{money(l.en_attente)} <span className="text-xs font-normal text-slate-500">({l.nb_en_attente})</span></Td>
    <Td>{pct(l.taux)}</Td>
  </tr>
);

const Liste = ({ demandes, annee }) => (
  <div className="-mx-5 mt-6 overflow-x-auto">
    <h3 className="mb-2 px-5 text-xs font-semibold uppercase tracking-wider text-emerald-800">Demandes de {annee}</h3>
    <SmartTable id="bilan-reclamations-liste" data-export-titre={`Demandes de ${annee}`} className="w-full min-w-[960px] text-sm" data-testid="bilan-reclamations-liste">
      <thead><tr className="text-[11px] font-mono uppercase tracking-wider text-slate-500"><Th left>Séjour</Th><Th left>Plateforme</Th><Th left>Réservation</Th><Th left>Envoyée le</Th>
        <Th>Réclamé</Th><Th>Accepté</Th><Th left>Statut</Th></tr></thead>
      <tbody>{demandes.map((d) => (
        <tr key={d.id} className="border-t border-slate-100" data-testid={`bilan-reclam-demande-${d.id}`}>
          <td className="whitespace-nowrap px-3 py-2.5 tabular">{d.resume?.date_debut || "—"}</td>
          <td className="px-3 py-2.5"><PlatformBadge plateforme={d.plateforme} /></td>
          <td className="px-3 py-2.5">{d.resume?.code || "Calcul libre"}<span className="block text-xs text-slate-500">{[d.resume?.nom_client, d.resume?.logement].filter(Boolean).join(" · ")}</span></td>
          <td className="whitespace-nowrap px-3 py-2.5 tabular">{d.date_envoi}</td>
          <Td>{money(Math.max(d.montant, 0))}</Td><Td cls="text-emerald-700">{money(d.accepte)}</Td>
          <td className="px-3 py-2.5">{STATUT[d.statut]}</td>
        </tr>
      ))}</tbody>
    </SmartTable>
  </div>
);

export const BilanReclamationsPanel = ({ annee }) => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/demandes/bilan", { params: { annee } }).then((r) => setData(r.data)).catch(() => setData(null)); }, [annee]);
  if (!data) { return null; }
  return (
    <Panel title="Réclamations auprès d'Airbnb et de Booking" testId="bilan-reclamations-panel">
      {!data.demandes.length ? (
        <p className="text-sm text-slate-500" data-testid="bilan-reclamations-vide">Aucune demande pour les séjours de {annee}. <Link to="/demandes" className="text-emerald-700 hover:underline">Voir les demandes</Link></p>
      ) : (<>
        <div className="-mx-5 overflow-x-auto">
          <SmartTable id="bilan-reclamations" data-export-titre={`Réclamations ${annee}`} className="w-full min-w-[820px] text-sm" data-testid="bilan-reclamations-table">
            <thead><tr className="text-[11px] font-mono uppercase tracking-wider text-slate-500"><Th left>Plateforme</Th><Th>Demandes</Th><Th>Réclamé</Th><Th>Accepté</Th><Th>Refusé</Th><Th>En attente</Th><Th>Taux obtenu</Th></tr></thead>
            <tbody>{data.plateformes.map((l) => <LigneTotal key={l.plateforme} l={l} />)}<LigneTotal l={data.total} total /></tbody>
          </SmartTable>
        </div>
        <p className="mt-2 text-xs text-slate-500">Année du séjour (année d'envoi pour un calcul sans date de séjour). Accepté = montant obtenu indiqué dans la réponse, sinon le montant réclamé. Taux obtenu = accepté ÷ réclamé.</p>
        <Liste demandes={data.demandes} annee={annee} />
      </>)}
    </Panel>
  );
};
